import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLancerGenerationMasse } from '../hooks/useBulletins';
import { useBulletinsStore } from '../store/bulletins.store';
import { PeriodeSelector } from '../components/PeriodeSelector';
import { useProjets } from '../../administration/hooks/useProjets';
import { useConfirm } from '../../../shared/components/ConfirmDialog/useConfirm';

export function BulletinsMassePage() {
  const navigate = useNavigate();
  const lancerMasse = useLancerGenerationMasse();
  const { filtres, setFiltres, setJobEnCours } = useBulletinsStore();

  const { data: projets } = useProjets({ actif: true });
  const listeProjets = (projets as any[]) ?? [];

  const AUCUN_PROJET = '__AUCUN__';
  const projetSelectionne = listeProjets.find((p: any) => p.idProjet === filtres.idProjet);
  const confirm = useConfirm();

  const handleLancer = async () => {
    const nbMois = filtres.mois.toString().padStart(2, '0');
    const ligneProjet = projetSelectionne
      ? projetSelectionne.nom
      : filtres.sansProjet
        ? 'les employés sans projet'
        : 'tous les employés actifs';

    const ok = await confirm({
      title: 'Lancer la génération en masse ?',
      message: `Période : ${nbMois}/${filtres.annee} — ${ligneProjet}`,
      detail: 'Les bulletins déjà existants pour cette période seront ignorés.',
      variant: 'info',
      labelConfirm: 'Lancer',
    });
    if (!ok) return;

    const res = await lancerMasse.mutateAsync({
      mois: filtres.mois,
      annee: filtres.annee,
      idProjet: filtres.idProjet || undefined,
      sansProjet: filtres.sansProjet || undefined,
    });
    setJobEnCours({ jobId: res.jobId, progression: 0, total: 0, erreurs: 0 });
    navigate(`/paie/bulletins/jobs/${res.jobId}`);
  };

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold text-content mb-6">Génération en masse</h1>

      <div className="bg-surface rounded-lg border border-border p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-content-muted mb-2">Période</label>
          <PeriodeSelector mois={filtres.mois} annee={filtres.annee} onChange={(m, a) => setFiltres({ mois: m, annee: a })} />
        </div>

        {listeProjets.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-content-muted mb-2">Projet</label>
            <select
              value={filtres.sansProjet ? AUCUN_PROJET : (filtres.idProjet ?? '')}
              onChange={e => {
                const val = e.target.value;
                if (val === AUCUN_PROJET) {
                  setFiltres({ idProjet: undefined, sansProjet: true });
                } else {
                  setFiltres({ idProjet: val || undefined, sansProjet: undefined });
                }
              }}
              className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Tous les employés actifs</option>
              <option value={AUCUN_PROJET}>Aucun projet</option>
              {listeProjets.map((p: any) => (
                <option key={p.idProjet} value={p.idProjet}>{p.nom}</option>
              ))}
            </select>
            <p className="text-xs text-content-subtle mt-1">
              Chaque projet peut avoir une date de paie différente — sélectionnez le projet concerné.
            </p>
          </div>
        )}

        <p className="text-sm text-content-muted bg-accent-light p-3 rounded border border-primary-light">
          {projetSelectionne
            ? <>La génération sera lancée pour les employés du projet <strong>{projetSelectionne.nom}</strong> sur la période {filtres.mois}/{filtres.annee}.</>
            : filtres.sansProjet
              ? <>La génération sera lancée pour les employés <strong>sans projet</strong> sur la période {filtres.mois}/{filtres.annee}.</>
              : <>La génération sera lancée pour <strong>tous les employés actifs</strong> sur la période {filtres.mois}/{filtres.annee}.</>
          }
          {' '}Vous serez redirigé vers la page de suivi.
        </p>

        {lancerMasse.isError && (
          <p className="text-danger text-sm bg-danger-light p-3 rounded">{(lancerMasse.error as any)?.message ?? 'Erreur'}</p>
        )}

        <div className="flex gap-3">
          <button
            onClick={handleLancer}
            disabled={lancerMasse.isPending}
            className="flex-1 bg-accent text-content-inverted py-2 rounded font-medium hover:bg-primary-hover disabled:opacity-50"
          >
            {lancerMasse.isPending ? 'Lancement...' : 'Lancer la génération'}
          </button>
          <button onClick={() => navigate(-1)} className="px-4 py-2 border border-border rounded text-sm hover:bg-surface-raised">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
